import { useEffect, useState } from 'react';
import { CATALOG_SORTS, type CatalogSort } from '@xshop/shared';

// A listing's choices live in the address — ?sort=price-asc&type=Soins&page=2 — so a filtered page can be
// shared, reloaded and walked back through with the browser's own Back button, as Sleek's can. Every listing
// element reads the same address; whichever changes it fires one event and they all redraw together.

/** What the address says the shopper chose: the order, the narrowing, and the page. */
export type ListingAddress = {
  sort: CatalogSort | null; available: boolean; priceMin: number | null; priceMax: number | null;
  type: string[]; vendor: string[]; option: string[]; page: number;
};

/** The event a listing element fires after changing the address, so the others redraw with it. */
export const LISTING_CHANGED = 'xe:listing';

/** The narrowings a shopper can tick, each a comma list in the address. */
export const LISTED_FACETS = ['type', 'vendor', 'option'] as const;
export type ListedFacet = (typeof LISTED_FACETS)[number];

// Reads one comma list from the address, without empty entries.
const listOf = (params: URLSearchParams, key: string): string[] =>
  (params.get(key) ?? '').split(',').map((value) => value.trim()).filter(Boolean);

// Reads a price from the address, or nothing for a blank, a word or a negative number.
const priceOf = (raw: string | null): number | null => {
  const value = raw === null || raw.trim() === '' ? NaN : Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

// Reads what an address says the shopper chose.
export function readListingAddress(search: string): ListingAddress {
  const params = new URLSearchParams(search);
  const sort = params.get('sort') ?? '';
  return {
    sort: (CATALOG_SORTS as readonly string[]).includes(sort) ? (sort as CatalogSort) : null,
    available: params.get('available') === 'true',
    priceMin: priceOf(params.get('priceMin')), priceMax: priceOf(params.get('priceMax')),
    type: listOf(params, 'type'), vendor: listOf(params, 'vendor'), option: listOf(params, 'option'),
    page: Math.max(1, Math.trunc(Number(params.get('page')) || 1))
  };
}

// Writes the choices into address parameters, leaving out defaults and keeping any others, such as q.
export function listingParams(address: ListingAddress, base: string): URLSearchParams {
  const params = new URLSearchParams(base);
  const put = (key: string, value: string | null) => (value ? params.set(key, value) : params.delete(key));
  put('sort', address.sort);
  put('available', address.available ? 'true' : null);
  put('priceMin', address.priceMin === null ? null : String(address.priceMin));
  put('priceMax', address.priceMax === null ? null : String(address.priceMax));
  for (const facet of LISTED_FACETS) put(facet, address[facet].join(',') || null);
  put('page', address.page > 1 ? String(address.page) : null);
  return params;
}

// Builds the link to the listing with these choices, from the current page.
export function listingHref(address: ListingAddress): string {
  const query = listingParams(address, window.location.search).toString();
  return `${window.location.pathname}${query ? `?${query}` : ''}`;
}

// Puts new choices in the address, as a step Back can undo, and tells every listing element.
export function goToListing(address: ListingAddress): void {
  window.history.pushState(null, '', listingHref(address));
  window.dispatchEvent(new Event(LISTING_CHANGED));
}

// Says whether the shopper has narrowed the listing at all.
export const isNarrowed = (address: ListingAddress): boolean =>
  address.available || address.priceMin !== null || address.priceMax !== null || LISTED_FACETS.some((facet) => address[facet].length > 0);

// Gives the same listing with every narrowing removed, keeping its order, from page one.
export const withoutNarrowing = (address: ListingAddress): ListingAddress =>
  ({ ...address, available: false, priceMin: null, priceMax: null, type: [], vendor: [], option: [], page: 1 });

// Gives the listing with one value ticked or unticked, from page one, since the pages change under it.
export function toggled(address: ListingAddress, facet: ListedFacet, value: string): ListingAddress {
  const chosen = address[facet].includes(value) ? address[facet].filter((entry) => entry !== value) : [...address[facet], value];
  return { ...address, [facet]: chosen, page: 1 };
}

// Follows the address: what the shopper chose, updated on Back, Forward and every listing change.
export function useListingAddress(): ListingAddress {
  const [address, setAddress] = useState(() => readListingAddress(window.location.search));
  useEffect(() => {
    const follow = () => setAddress(readListingAddress(window.location.search));
    window.addEventListener('popstate', follow);
    window.addEventListener(LISTING_CHANGED, follow);
    return () => { window.removeEventListener('popstate', follow); window.removeEventListener(LISTING_CHANGED, follow); };
  }, []);
  return address;
}
